import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, EP } from '../api'
import { useAuth } from '../auth'
import type { CartItem, CartResponse } from '../types'

export const useCartStore = defineStore('cart', () => {
  const auth = useAuth()

  const items = ref<CartItem[]>([])
  const total = ref(0)
  const tax = ref(0)
  const grandTotal = ref(0)
  const currency = ref('MMK')
  const count = ref(0)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isEmpty = computed(() => items.value.length === 0)

  async function fetchCart() {
    if (!auth.isLoggedIn || !auth.isUserx) return
    loading.value = true
    error.value = null
    try {
      const res = await api<CartResponse>(EP.CART_GET, {}, auth.getToken())
      items.value = res.items
      total.value = res.total
      tax.value = res.tax
      grandTotal.value = res.grand_total
      currency.value = res.currency
      count.value = res.count
    } catch {
      // silently fail — cart is not critical
    } finally {
      loading.value = false
    }
  }

  async function addItem(itemxI: number, quantity = 1) {
    loading.value = true
    error.value = null
    try {
      const res = await api<CartResponse>(EP.CART_ADD, { itemx_i: itemxI, quantity }, auth.getToken())
      items.value = res.items
      total.value = res.total
      tax.value = res.tax
      grandTotal.value = res.grand_total
      currency.value = res.currency
      count.value = res.count
    } catch (e: any) {
      error.value = e.code ?? e.message ?? 'add_to_cart_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateItem(cartId: number, quantity: number) {
    loading.value = true
    error.value = null
    try {
      const res = await api<CartResponse>(EP.CART_UPDATE, { cart_id: cartId, quantity }, auth.getToken())
      items.value = res.items
      total.value = res.total
      tax.value = res.tax
      grandTotal.value = res.grand_total
      currency.value = res.currency
      count.value = res.count
    } catch (e: any) {
      error.value = e.code ?? e.message ?? 'update_cart_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function removeItem(cartId: number) {
    loading.value = true
    error.value = null
    try {
      const res = await api<CartResponse>(EP.CART_REMOVE, { cart_id: cartId }, auth.getToken())
      items.value = res.items
      total.value = res.total
      tax.value = res.tax
      grandTotal.value = res.grand_total
      currency.value = res.currency
      count.value = res.count
    } catch (e: any) {
      error.value = e.code ?? e.message ?? 'remove_cart_item_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function clear() {
    loading.value = true
    error.value = null
    try {
      const res = await api<CartResponse>(EP.CART_CLEAR, {}, auth.getToken())
      items.value = res.items
      total.value = res.total
      tax.value = res.tax
      grandTotal.value = res.grand_total
      currency.value = res.currency
      count.value = res.count
    } catch (e: any) {
      error.value = e.code ?? e.message ?? 'clear_cart_failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  function $reset() {
    items.value = []
    total.value = 0
    tax.value = 0
    grandTotal.value = 0
    count.value = 0
    error.value = null
  }

  return {
    items,
    total,
    tax,
    grandTotal,
    currency,
    count,
    loading,
    error,
    isEmpty,
    fetchCart,
    addItem,
    updateItem,
    removeItem,
    clear,
    $reset,
  }
})
